import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  IconButton,
} from "@chakra-ui/react";
import { Account, Team } from "common";
import { useCallback, useState } from "react";
import { AccountCard } from "../account/AccountCard";
import { useAPI } from "../api/useAPI";
import { useAPIQuery } from "../api/useAPIQuery";
import { useOnAPISuccess } from "../api/useOnAPISuccess";
import { useToastOnAPIError } from "../api/useToastOnAPIError";
import { AccountSelector } from "../utils/AccountSelector";
import { THEME_COLOR_SCHEME } from "../utils/theme";
import { IoMdClose } from "react-icons/io";
import { useGetTeamLeaders } from "./useGetTeamLeaders";

interface TeamLeaderManageModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team;
  onSuccess: () => void;
}

export const TeamLeaderManageModal = ({
  isOpen,
  onClose,
  team,
  onSuccess,
}: TeamLeaderManageModalProps) => {
  const api = useAPI();
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const teamLeaders = useGetTeamLeaders({ team });

  const {
    data: leaderPostData,
    state: leaderPostState,
    error: leaderPostError,
    query: leaderPostQuery,
  } = useAPIQuery<"teamLeaderPost">({
    apiCall: api.teamLeaderPost,
  });
  useToastOnAPIError({ title: "Unable to add leader", error: leaderPostError });
  useOnAPISuccess<"teamLeaderPost">({
    data: leaderPostData,
    state: leaderPostState,
    onSuccess,
  });

  const {
    data: leaderDeleteData,
    state: leaderDeleteState,
    error: leaderDeleteError,
    query: leaderDeleteQuery,
  } = useAPIQuery<"teamLeaderDelete">({
    apiCall: api.teamLeaderDelete,
  });
  useToastOnAPIError({
    title: "Unable to delete leader",
    error: leaderDeleteError,
  });
  useOnAPISuccess<"teamLeaderDelete">({
    data: leaderDeleteData,
    state: leaderDeleteState,
    onSuccess,
  });

  const addLeader = useCallback(() => {
    if (selectedAccount) {
      leaderPostQuery({ teamId: team.id, accountId: selectedAccount.id });
    }
  }, [leaderPostQuery, selectedAccount, team.id]);

  const filterAddedAccounts = useCallback(
    (account: Account) => !team.leaderIds.includes(account.id),
    [team.leaderIds]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={"xl"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Manage Team Leaders</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box display={"flex"} width={"100%"}>
            <AccountSelector
              account={selectedAccount}
              setAccount={setSelectedAccount}
              filterAccount={filterAddedAccounts}
              marginRight={2}
            />
            <Button
              colorScheme={THEME_COLOR_SCHEME}
              isDisabled={selectedAccount === null}
              onClick={addLeader}
            >
              Add
            </Button>
          </Box>
          <Box paddingTop={4}>
            {teamLeaders.map((account) => (
              <Box key={account.id} position={"relative"}>
                <AccountCard clickable={false} account={account} size={"100"} />
                <IconButton
                  icon={<IoMdClose />}
                  aria-label="remove leader"
                  position={"absolute"}
                  top={2}
                  right={2}
                  size={"sm"}
                  variant={"outline"}
                  onClick={() =>
                    leaderDeleteQuery({
                      teamId: team.id,
                      accountId: account.id,
                    })
                  }
                />
              </Box>
            ))}
          </Box>
        </ModalBody>
        <ModalFooter></ModalFooter>
      </ModalContent>
    </Modal>
  );
};
